import Styles from './FeedbackMessage.module.css';
import { Label } from '@app/engine';
import { useEffect, useRef, useState } from 'react';

/** File-local constants for FeedbackMessage. */
const localConstants = {
  // Base display time before the fade begins.
  displayBaseMs: 8000,
  // Additional display time per message character - longer messages stay on longer.
  displayPerCharacterMs: 50,
  scrollOptions: { behavior: 'smooth', block: 'nearest' } as const
} as const;

/** A transient panel feedback payload. A fresh object instance must be set per event so repeated identical messages re-trigger the fade. */
export interface IFeedbackDTO {
  text: string;
  type: 'success' | 'error';
}

/**
 * Computes the display duration for a message: a fixed base plus a per-character addition, so long messages stay readable (matches the Vertex Arena FadeMessage formula).
 *
 * @param text - The message text.
 * @returns The display duration in milliseconds.
 */
function GetDisplayDurationMs(text: string): number {
  return localConstants.displayBaseMs + text.length * localConstants.displayPerCharacterMs;
}

/**
 * A transient success/error strip shown beneath a panel header (full width, bordered top and bottom): appears on each feedback event, scrolls itself into view, fades out via CSS, and unmounts
 * once its duration elapses. The duration scales with message length (see {@link GetDisplayDurationMs}) so long server errors stay readable. Unlike the portal's FadeMessage it never jumps to the
 * top of the page — `block: 'nearest'` makes the scroll a no-op when the strip is already visible and otherwise nudges just far enough to reveal it.
 *
 * @param props - Component props.
 * @param props.feedback - The feedback to show, or null for none. Each event must pass a new object instance (not a reused one) so identical consecutive messages still restart the timer.
 * @returns The feedback element, or null when no message is active.
 */
export function FeedbackMessage({ feedback }: { feedback: IFeedbackDTO | null }) {
  const [display, setDisplay] = useState<IFeedbackDTO | null>(null);
  const messageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setDisplay(feedback);
    if (!feedback) return;
    const timer = setTimeout(() => setDisplay(null), GetDisplayDurationMs(feedback.text));
    return () => clearTimeout(timer);
  }, [feedback]);

  useEffect(() => {
    if (display) messageRef.current?.scrollIntoView(localConstants.scrollOptions);
  }, [display]);

  if (!display) return null;

  // The fade animation lives on the strip's class; its duration must match the unmount timer, so the char-scaled value is applied inline (the keyframes' percentage stops scale with it).
  return (
    <div ref={messageRef} className={display.type === 'success' ? Styles.success : Styles.error} style={{ animationDuration: `${GetDisplayDurationMs(display.text)}ms` }}>
      <Label text={display.text} />
    </div>
  );
}
